import React, { useState } from 'react';
import { CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PaymentAuditTrail = ({ auditData }) => {
    const [expandedBatches, setExpandedBatches] = useState({});

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const toggleBatch = (batchId) => {
        setExpandedBatches(prev => ({
            ...prev,
            [batchId]: !prev[batchId]
        }));
    };

    const getPaymentMethodColor = (method) => {
        const colors = {
            MPESA: 'bg-green-100 text-green-800',
            BANK_TRANSFER: 'bg-blue-100 text-blue-800',
            CHEQUE: 'bg-purple-100 text-purple-800',
            CASH: 'bg-yellow-100 text-yellow-800',
            OTHER: 'bg-gray-100 text-gray-800'
        };
        return colors[method] || 'bg-gray-100 text-gray-800';
    };

    const getStatusColor = (status) => {
        const colors = {
            CREATED: 'bg-yellow-100 text-yellow-800',
            POSTED: 'bg-green-100 text-green-800',
            ALLOCATED: 'bg-blue-100 text-blue-800',
            VERIFIED: 'bg-purple-100 text-purple-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    if (!auditData || !auditData.batches || auditData.batches.length === 0) {
        return (
            <div className="text-center py-12">
                <CreditCardIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No payment records found for this period</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Header */}
            <div className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Payment Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">Total Batches</div>
                        <div className="text-2xl font-bold text-[#2a2e30]">
                            {auditData.batches.length}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">Total Allocated</div>
                        <div className="text-2xl font-bold text-green-700">
                            {formatCurrency(
                                auditData.batches.reduce((sum, batch) => 
                                    sum + batch.allocations.reduce((s, a) => s + a.allocated_amount, 0), 0
                                )
                            )}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">Vehicles Paid</div>
                        <div className="text-2xl font-bold text-[#FF5733]">
                            {new Set(
                                auditData.batches.flatMap(batch => 
                                    batch.allocations.map(a => a.plate_no)
                                )
                            ).size}
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Batches */}
            <div className="space-y-4">
                {auditData.batches.map((batch) => (
                    <div key={batch.batch_id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                        {/* Batch Header */}
                        <div 
                            className="bg-gradient-to-r from-[#FF5733] to-[#e64520] p-4 text-white cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => toggleBatch(batch.batch_id)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                        <CreditCardIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-lg">
                                            {batch.batch_reference}
                                        </div>
                                        <div className="text-sm opacity-90">
                                            {new Date(batch.payment_date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                            {' • '}
                                            {batch.allocations.length} allocation{batch.allocations.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold">
                                        {formatCurrency(batch.batch_total)}
                                    </div>
                                    <div className="flex items-center gap-2 justify-end mt-1">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPaymentMethodColor(batch.payment_method)}`}>
                                            {batch.payment_method}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(batch.batch_status)}`}>
                                            {batch.batch_status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Batch Details */}
                        {expandedBatches[batch.batch_id] && (
                            <div className="p-6">
                                {/* Batch Info */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                                    <div>
                                        <div className="text-xs text-gray-600 mb-1">Paid By</div>
                                        <div className="font-semibold text-[#2a2e30]">{batch.paid_by}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600 mb-1">Cashbook Account</div>
                                        <div className="font-semibold text-[#2a2e30]">{batch.cashbook_account_id || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600 mb-1">Cashbook Entry</div>
                                        <div className="font-semibold text-[#2a2e30]">{batch.cashbook_entry_id || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600 mb-1">Status</div>
                                        <div className="font-semibold text-[#2a2e30]">{batch.batch_status}</div>
                                    </div>
                                </div>

                                {/* Allocations Table */}
                                <div>
                                    <h4 className="text-lg font-bold text-[#2a2e30] mb-3">Payment Allocations</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Vehicle</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Remittance ID</th>
                                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Payout Amount</th>
                                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Allocated</th>
                                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Approval Trans</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Payment Trans</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {batch.allocations.map((allocation) => (
                                                    <tr key={allocation.allocation_id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-semibold text-[#2a2e30]">
                                                            {allocation.plate_no}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            #{allocation.weekly_remittance_id}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                                                            {formatCurrency(allocation.owner_payout)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-semibold">
                                                            {formatCurrency(allocation.allocated_amount)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <div className="flex items-center justify-center gap-1">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(allocation.allocation_status)}`}>
                                                                    {allocation.allocation_status}
                                                                </span>
                                                                {allocation.allocation_status === 'VERIFIED' && (
                                                                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono">
                                                            {allocation.owner_trans_id_approval || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono">
                                                            {allocation.owner_trans_id_payment || 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                            {new Date(allocation.allocation_date).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-gray-50">
                                                <tr>
                                                    <td colSpan="3" className="px-4 py-3 text-right font-bold text-[#2a2e30]">
                                                        Batch Total:
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-bold text-[#FF5733]">
                                                        {formatCurrency(
                                                            batch.allocations.reduce((sum, a) => sum + a.allocated_amount, 0)
                                                        )}
                                                    </td>
                                                    <td colSpan="4"></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PaymentAuditTrail;
